import { Box, Divider, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import DotIcon from "../../../../assets/images/icons/DotIcon.tsx";
import PlusIcon from "../../../../assets/images/icons/PlusIcon.tsx";
import UnderscoreIcon from "../../../../assets/images/icons/UnderscoreIcon.tsx";
import { ORG_SUBSCRIPTION_PLANS } from "../../../../constants/billing.ts";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import BriaButton, { ButtonTypes } from "../../../common/BriaButton/BriaButton.tsx";
import LoadingPlaceholder from "../../../common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import styles from "./PricingCard.module.scss";

export interface IPricingCards {
	title: string;
	description: string;
	price: {
		title: string;
		description?: string;
	};
	buttonText: string;
	className?: string;
	buttonType?: ButtonTypes;
	planKey?: string;
	onFeatureClick?: (featureId: string | null) => void;
	selectedFeature?: string | null;
	features?: any;
	getFeatureContent?: (planKey: string, featureId: string) => any;
	maxExpandedHeight?: number;
	featureContentRefs?: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
	onButtonClick?: () => void;
}

const PricingCard = ({
	buttonType = "tertiaryMedium",
	className,
	planKey,
	onFeatureClick,
	selectedFeature,
	features,
	getFeatureContent,
	maxExpandedHeight,
	featureContentRefs,
	onButtonClick,
	...pricingCard
}: IPricingCards) => {
	const { t } = useTranslation("translation", { keyPrefix: "pricingFlow" });

	const { authStore } = useAppStore();

	const isLoading = authStore.isLoadingUserOrganizations || authStore.isLoadingOrgSubscriptions;
	const currentPlanName = authStore.orgSubscription?.plan_name.toLowerCase();

	const getFeatureTitle = (content: any) => {
		if (typeof content.title === "string") {
			return (
				<Box className={styles.titleWithIcon}>
					{content.title === "-" ? <UnderscoreIcon /> : <span>{content.title}</span>}
				</Box>
			);
		} else if (typeof content.title === "object") {
			return (
				<Box className={styles.featureTitlesContainer}>
					{content.features.map((feature: any, index: number) => (
						<Box key={index} className={styles.featureWithIcon}>
							<span>{feature.title}</span>
						</Box>
					))}
				</Box>
			);
		}

		return null;
	};

	const getCTAButtonText = (buttonText: string, planKey?: string) => {
		// Free Trial card logic
		if (planKey === "freeTrial") {
			if (currentPlanName === ORG_SUBSCRIPTION_PLANS.free.name) {
				return t("yourCurrentPlan");
			} else if (
				currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name ||
				currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name
			) {
				return null;
			}
		}

		// PAYG (selfService/starter) card logic
		if (planKey === "selfService") {
			if (currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name) {
				return authStore.orgSubscription?.cancels_on ? t("renewSubscription") : t("manageSubscription");
			} else if (currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
				return null;
			}
		}

		// Custom plan (briaService) card logic
		if (planKey === "briaService") {
			if (currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
				return authStore.orgSubscription?.cancels_on ? t("renewSubscription") : t("managePlan");
			}
		}

		return buttonText;
	};

	const shouldShowButton = (planKey?: string) => {
		// Free Trial: hide button if user has paid plan
		if (
			planKey === "freeTrial" &&
			(currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name ||
				currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name)
		) {
			return false;
		}

		// PAYG: hide button if user has custom plan
		if (planKey === "selfService" && currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
			return false;
		}

		return true;
	};

	const getButtonDisabled = (planKey?: string) => {
		if (authStore.orgSubscription?.is_marketplace) {
			return true;
		}
		// Free Trial: disable if user is already on free plan
		if (planKey === "freeTrial" && currentPlanName === ORG_SUBSCRIPTION_PLANS.free.name) {
			return true;
		}

		return false;
	};

	const getPricingCardTitle = (title: string) => {
		if (planKey === "briaService" && currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
			return t("yourCurrentPlan");
		}
		return title;
	};

	const getPricingCardPrice = (price: any) => {
		if (planKey === "selfService") {
			return (
				<Box className={styles.priceContainer}>
					<Box className={styles.priceItem}>
						<Typography className={styles.title}>{price.imageTitle}</Typography>
						<Typography className={styles.description}>{price.imageDescription}</Typography>
					</Box>
					<Divider orientation="vertical" flexItem className={styles.divider} />
					<Box className={styles.priceItem}>
						<Typography className={styles.title}>{price.videoTitle}</Typography>
						<Typography className={styles.description}>{price.videoDescription}</Typography>
					</Box>
				</Box>
			);
		}
		return (
			pricingCard.price.description && (
				<Typography className={styles.description}>{pricingCard.price.description}</Typography>
			)
		);
	};
	return (
		<Box className={clsx(styles.pricingCardContainer, className)}>
			<Box className={styles.pricingCardHeader}>
				<Typography className={styles.title}>{pricingCard.title}</Typography>
				<Typography className={styles.description}>{pricingCard.description}</Typography>
				<Box className={styles.cardPrice}>
					<Typography className={styles.title}>{getPricingCardTitle(pricingCard.price.title)}</Typography>
					{getPricingCardPrice(pricingCard.price)}
				</Box>
				<Box className={styles.buttonContainer}>
					{shouldShowButton(planKey) && !isLoading ? (
						<BriaButton
							className={clsx(styles.actionButton, {
								[styles.primary]: buttonType === "primaryMedium",
								[styles.disabled]: getButtonDisabled(planKey),
							})}
							buttonType={buttonType}
							onClick={onButtonClick}
							disabled={getButtonDisabled(planKey)}
						>
							{getCTAButtonText(pricingCard.buttonText, planKey)}
						</BriaButton>
					) : (
						<LoadingPlaceholder isLoading={isLoading} size={20} />
					)}
				</Box>
			</Box>

			{features && planKey && getFeatureContent && (
				<Box className={styles.featuresList}>
					{Object.entries(features).map(([featureId, _]: [string, any]) => {
						const content = getFeatureContent(planKey, featureId);
						const isExpanded = selectedFeature === featureId;

						return (
							<Box key={featureId} className={styles.featureItem}>
								{!isExpanded && (
									<Box
										className={clsx(styles.featureHeader, {
											[styles.expanded]: isExpanded,
											[styles.briaService]: planKey === "briaService",
										})}
										onClick={() => onFeatureClick?.(isExpanded ? null : featureId)}
									>
										<Typography className={styles.featureTitle}>
											{getFeatureTitle(content)}
										</Typography>
									</Box>
								)}
								{content.features?.length && (
									<Box
										className={clsx(styles.expandIcon, {
											[styles.expanded]: isExpanded,
										})}
										onClick={() => onFeatureClick?.(isExpanded ? null : featureId)}
									>
										<PlusIcon />
									</Box>
								)}

								{isExpanded && (
									<Box
										className={styles.featureContent}
										ref={(el) => {
											if (planKey && featureContentRefs) {
												featureContentRefs.current[`${planKey}-${featureId}`] =
													el as HTMLDivElement;
											}
										}}
										style={{
											minHeight:
												maxExpandedHeight &&
												maxExpandedHeight > 0 &&
												selectedFeature === featureId
													? `${maxExpandedHeight}px`
													: "auto",
										}}
									>
										{typeof content.title === "string" && (
											<Typography className={styles.featureContentTitle}>
												{getFeatureTitle(content)}
											</Typography>
										)}
										{content.features?.map((featureGroup: any, index: number) => (
											<Box key={index} className={styles.featureGroup}>
												{featureGroup.title && (
													<Typography className={styles.featureGroupTitle}>
														{featureGroup.title}
													</Typography>
												)}
												<Box className={styles.featureItems}>
													{featureGroup.items?.map((item: string, itemIndex: number) => (
														<Box key={itemIndex} className={styles.featureItemText}>
															<Box className={styles.dotIcon}>
																<DotIcon />
															</Box>
															<Typography>{item}</Typography>
														</Box>
													))}
												</Box>
											</Box>
										))}
									</Box>
								)}
							</Box>
						);
					})}
				</Box>
			)}
		</Box>
	);
};

export default observer(PricingCard);
